import {
  MoveRight,
  Phone,
  Upload,
  X,
  FileText,
  CircleCheck,
} from "lucide-react";
import { useState, useCallback, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { images } from "../../assets/assets";
import Navbar from "../../Components/Navbar";
import Footer from "../../Components/Footer";
import { getJobId, applyToJob } from "../../api";
// import {  pageSEO } from "../../utils/seo";

const ApplicationForm = () => {
  // useEffect(() => {
  //   updateSEO(pageSEO.career);
  // }, []);

  const { jobId } = useParams();
  const [file, setFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState("");
  const [jobError, setJobError] = useState("");
  const [jobLoading, setJobLoading] = useState(!!jobId);
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [formErrors, setFormErrors] = useState({});
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phoneNumber: "",
    position: "",
    experience: "",
    qualification: "",
  });

  // Fetch job details if jobId is provided
  useEffect(() => {
    if (jobId) {
      const fetchJobDetails = async () => {
        try {
          setJobLoading(true);
          setJobError("");
          const jobData = await getJobId(jobId);

          if (jobData?.data?.title) {
            setFormData((prev) => ({
              ...prev,
              position: jobData.data.title,
            }));
            setJobError("");
          } else {
            setJobError("Job not available. Cannot proceed with application.");
            setFormData((prev) => ({
              ...prev,
              position: "",
            }));
          }
        } catch (err) {
          console.error("Error fetching job details:", err);
          setJobError("Job not available. Cannot proceed with application.");
          setFormData((prev) => ({
            ...prev,
            position: "",
          }));
        } finally {
          setJobLoading(false);
        }
      };

      fetchJobDetails();
    }
  }, [jobId]);

  const validateFile = (f) => {
    const allowed = [
      "application/pdf",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];
    if (!allowed.includes(f.type)) {
      setError("Only PDF or DOCX files are allowed.");
      return false;
    }
    if (f.size > 5 * 1024 * 1024) {
      setError("File size must be under 5MB.");
      return false;
    }
    return true;
  };

  const validateForm = () => {
    const errors = {};

    // Full Name validation
    if (!formData.fullName.trim()) {
      errors.fullName = "Full Name is required";
    } else if (formData.fullName.trim().length < 3) {
      errors.fullName = "Full Name must be at least 3 characters";
    }

    // Email validation
    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = "Please enter a valid email address";
    }

    // Phone validation
    if (!formData.phoneNumber.trim()) {
      errors.phoneNumber = "Phone Number is required";
    } else if (!/^\d{10}$/.test(formData.phoneNumber.replace(/\D/g, ""))) {
      errors.phoneNumber = "Please enter a valid 10-digit phone number";
    }

    // Position validation
    if (!formData.position.trim()) {
      errors.position = "Position is required";
    }

    // Experience validation
    if (!formData.experience.trim()) {
      errors.experience = "Years of Experience is required";
    } else if (isNaN(formData.experience) || formData.experience < 0) {
      errors.experience = "Please enter a valid number";
    }

    // Qualification validation
    if (!formData.qualification.trim()) {
      errors.qualification = "Highest Qualification is required";
    }

    // File validation
    if (!file) {
      errors.resume = "Resume is required";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error for this field when user starts typing
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setSuccessMessage("");

    if (!validateForm()) {
      console.log("❌ Form validation failed:", formErrors);
      return;
    }

    setSubmitting(true);
    try {
      // Prepare FormData with file
      const formDataToSend = new FormData();
      formDataToSend.append("fullName", formData.fullName);
      formDataToSend.append("email", formData.email);
      formDataToSend.append("phoneNumber", formData.phoneNumber);
      formDataToSend.append("position", formData.position);
      formDataToSend.append("experience", formData.experience);
      formDataToSend.append("qualification", formData.qualification);
      if (jobId) {
        formDataToSend.append("jobId", jobId);
      }
      if (file) {
        formDataToSend.append("resume", file);
      }

      console.log("✅ Application Form Submitted!");
      console.log("Form Data:", formData);
      console.log("File:", file?.name);

      const response = await applyToJob(formDataToSend);

      // Show success message from API response
      setSuccessMessage(
        response?.message || "Application submitted successfully!",
      );
      console.log("Form submission successful!");

      // Reset form
      setFormData({
        fullName: "",
        email: "",
        phoneNumber: "",
        position: "",
        experience: "",
        qualification: "",
      });
      setFile(null);
      setError("");
    } catch (err) {
      // Show error message
      const errorMsg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to submit application. Please try again.";
      setErrorMessage(errorMsg);
      console.error("❌ Error submitting application:", err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleFile = (f) => {
    setError("");
    if (validateFile(f)) {
      setFile(f);
      // Clear resume error if file is successfully added
      if (formErrors.resume) {
        setFormErrors((prev) => ({
          ...prev,
          resume: "",
        }));
      }
    }
  };

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) handleFile(dropped);
  }, []);

  const onDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const onDragLeave = () => setIsDragging(false);

  const removeFile = (e) => {
    e.stopPropagation();
    setFile(null);
    setError("");
    if (formErrors.resume) {
      setFormErrors((prev) => ({
        ...prev,
        resume: "",
      }));
    }
  };
  return (
    <div className="font-lato">
      <Navbar />
      {/* Top Page heading banner */}
      <div className="bg-[#F7F7F7B2] hover:bg-[#EFEFEF] active:bg-[#E8E8E8] py-3 sm:py-4 md:py-6 flex justify-center px-4 transition-colors duration-200">
        <div className="flex flex-col sm:flex-row justify-center sm:justify-start gap-2 sm:gap-4 items-center max-w-[1100px]">
          <img
            src={images.Logo}
            alt="logo"
            className="w-[50px] h-[50px] sm:w-[80px] sm:h-[80px] md:w-[95px] md:h-[95px] flex-shrink-0 hover:scale-105 transition-transform duration-300"
          />
          <div className="max-w-[500px]">
            <p className="font-bold text-[16px] sm:text-[20px] md:text-[24px] text-center sm:text-left">
              Job Application Form
            </p>
            <p className="font-openSans text-[12px] sm:text-[14px] md:text-[16px] leading-[1.2] text-center sm:text-left text-gray-700">
              Fill in your details below to apply and take the next step in your
              healthcare career with us.
            </p>
          </div>
        </div>
      </div>

      {/* Job Error Alert */}
      {jobError && (
        <div className="px-3 pt-4">
          <div className="max-w-[750px] mx-auto bg-red-50 border border-red-200 rounded-[16px] p-4 sm:p-5">
            <p className="text-red-600 font-semibold text-[14px] sm:text-[15px]">
              ⚠️ {jobError}
            </p>
            <p className="text-red-500 text-[12px] sm:text-[13px] mt-2">
              Please go back to{" "}
              <Link
                to="/career"
                className="font-semibold underline hover:text-red-700"
              >
                Career page
              </Link>{" "}
              to select a valid position.
            </p>
          </div>
        </div>
      )}
      {/* form */}
      <div className="px-3">
        <div className="font-poppins max-w-[750px] mx-auto my-4 md:my-6 border-t-[4px] sm:border-t-[5px] md:border-t-[6px] border-[#0090FF] shadow-[0px_0px_12px_2px_#0000000D] px-4 sm:px-6 py-5 rounded-[20px] md:rounded-[30px] hover:shadow-[0px_0px_20px_4px_#0000001A] transition-shadow duration-300 ">
          <h3 className="text-[#1F2933] text-[18px] sm:text-[20px] font-medium">
            Send Us a Message
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4 md:mt-5">
            <div className="flex flex-col">
              <label
                htmlFor="fullName"
                className="text-[#1F2933] font-medium text-sm sm:text-base mb-2"
              >
                Full Name*
              </label>
              <input
                type="text"
                id="fullName"
                name="fullName"
                value={formData.fullName}
                onChange={handleInputChange}
                className={`bg-[#F8F8F8] py-2.5 sm:py-3.5 px-4 sm:px-5 rounded-[20px] md:rounded-[24px] text-[13px] sm:text-[14px] text-[#9C9C9C] placeholder:text-[#9C9C9C] hover:bg-[#F0F0F0] focus:bg-white focus:outline-none focus:ring-2 transition-all duration-200 ${
                  formErrors.fullName
                    ? "focus:ring-red-400 ring-2 ring-red-300"
                    : "focus:ring-[#0090FF]"
                }`}
                placeholder="Enter your full name"
              />
              {formErrors.fullName && (
                <p className="text-red-500 text-xs mt-1">
                  {formErrors.fullName}
                </p>
              )}
            </div>
            <div className="flex flex-col">
              <label
                htmlFor="email"
                className="text-[#1F2933] font-medium text-sm sm:text-base mb-2"
              >
                Email Address*
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={`bg-[#F8F8F8] py-2.5 sm:py-3.5 px-4 sm:px-5 rounded-[20px] md:rounded-[24px] text-[13px] sm:text-[14px] text-[#9C9C9C] placeholder:text-[#9C9C9C] hover:bg-[#F0F0F0] focus:bg-white focus:outline-none focus:ring-2 transition-all duration-200 ${
                  formErrors.email
                    ? "focus:ring-red-400 ring-2 ring-red-300"
                    : "focus:ring-[#0090FF]"
                }`}
                placeholder="Enter your email address"
              />
              {formErrors.email && (
                <p className="text-red-500 text-xs mt-1">{formErrors.email}</p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3 md:mt-4">
            <div className="flex flex-col">
              <label
                htmlFor="phoneNumber"
                className="text-[#1F2933] font-medium text-sm sm:text-base mb-2"
              >
                Phone Number*
              </label>
              <input
                type="text"
                id="phoneNumber"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                className={`bg-[#F8F8F8] py-2.5 sm:py-3.5 px-4 sm:px-5 rounded-[20px] md:rounded-[24px] text-[13px] sm:text-[14px] text-[#9C9C9C] placeholder:text-[#9C9C9C] hover:bg-[#F0F0F0] focus:bg-white focus:outline-none focus:ring-2 transition-all duration-200 ${
                  formErrors.phoneNumber
                    ? "focus:ring-red-400 ring-2 ring-red-300"
                    : "focus:ring-[#0090FF]"
                }`}
                placeholder="Enter your phone number"
              />
              {formErrors.phoneNumber && (
                <p className="text-red-500 text-xs mt-1">
                  {formErrors.phoneNumber}
                </p>
              )}
            </div>
            <div className="flex flex-col">
              <label
                htmlFor="position"
                className="text-[#1F2933] font-medium text-sm sm:text-base mb-2"
              >
                Position applied for*
              </label>
              <input
                type="text"
                id="position"
                name="position"
                value={formData.position}
                onChange={handleInputChange}
                readOnly={!!jobId}
                className={`bg-[#F8F8F8] py-2.5 sm:py-3.5 px-4 sm:px-5 rounded-[20px] md:rounded-[24px] text-[13px] sm:text-[14px] text-[#9C9C9C] placeholder:text-[#9C9C9C] hover:bg-[#F0F0F0] focus:bg-white focus:outline-none focus:ring-2 transition-all duration-200 ${
                  formErrors.position
                    ? "focus:ring-red-400 ring-2 ring-red-300"
                    : "focus:ring-[#0090FF]"
                } ${jobId ? "bg-gray-100 cursor-not-allowed" : ""}`}
                placeholder="Senior Medical Officer"
                disabled={jobLoading}
              />
              {jobLoading && (
                <p className="text-blue-500 text-xs mt-1">
                  Loading job details...
                </p>
              )}
              {formErrors.position && !jobLoading && (
                <p className="text-red-500 text-xs mt-1">
                  {formErrors.position}
                </p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3 md:mt-4">
            <div className="flex flex-col">
              <label
                htmlFor="experience"
                className="text-[#1F2933] font-medium text-sm sm:text-base mb-2"
              >
                Years of Experience*
              </label>
              <input
                type="text"
                id="experience"
                name="experience"
                value={formData.experience}
                onChange={handleInputChange}
                className={`bg-[#F8F8F8] py-2.5 sm:py-3.5 px-4 sm:px-5 rounded-[20px] md:rounded-[24px] text-[13px] sm:text-[14px] text-[#9C9C9C] placeholder:text-[#9C9C9C] hover:bg-[#F0F0F0] focus:bg-white focus:outline-none focus:ring-2 transition-all duration-200 ${
                  formErrors.experience
                    ? "focus:ring-red-400 ring-2 ring-red-300"
                    : "focus:ring-[#0090FF]"
                }`}
                placeholder="5 years"
              />
              {formErrors.experience && (
                <p className="text-red-500 text-xs mt-1">
                  {formErrors.experience}
                </p>
              )}
            </div>
            <div className="flex flex-col">
              <label
                htmlFor="qualification"
                className="text-[#1F2933] font-medium text-sm sm:text-base mb-2"
              >
                Highest Qualification*
              </label>
              <input
                type="text"
                id="qualification"
                name="qualification"
                value={formData.qualification}
                onChange={handleInputChange}
                className={`bg-[#F8F8F8] py-2.5 sm:py-3.5 px-4 sm:px-5 rounded-[20px] md:rounded-[24px] text-[13px] sm:text-[14px] text-[#9C9C9C] placeholder:text-[#9C9C9C] hover:bg-[#F0F0F0] focus:bg-white focus:outline-none focus:ring-2 transition-all duration-200 ${
                  formErrors.qualification
                    ? "focus:ring-red-400 ring-2 ring-red-300"
                    : "focus:ring-[#0090FF]"
                }`}
                placeholder="MBBS, MD (General Medicine)"
              />
              {formErrors.qualification && (
                <p className="text-red-500 text-xs mt-1">
                  {formErrors.qualification}
                </p>
              )}
            </div>
          </div>
          {/* Resume Upload */}
          <div className="flex flex-col mt-4 md:mt-5">
            <label className="text-[#1F2933] font-medium text-sm sm:text-base mb-2">
              Upload Resume*
            </label>

            <label
              htmlFor="resume"
              onDrop={onDrop}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              className={`border-2 border-dashed rounded-[16px] md:rounded-2xl p-4 sm:p-6 text-center bg-[#F8F8F8B2] cursor-pointer transition-all duration-200
      ${
        isDragging
          ? "border-[#1975FF] bg-[#EEF4FF]"
          : file
            ? "border-green-400 bg-green-50"
            : "border-[#D9D9D9] hover:bg-gray-50 hover:border-[#0090FF]"
      }`}
            >
              {file ? (
                // File selected state
                <div className="flex items-center justify-between px-2 sm:px-3">
                  <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                    <FileText className="text-[#1975FF] w-6 h-6 sm:w-8 sm:h-8 flex-shrink-0" />
                    <div className="text-left min-w-0">
                      <p className="text-xs sm:text-sm font-semibold text-[#1F2933] truncate">
                        {file.name}
                      </p>
                      <p className="text-[10px] sm:text-xs text-gray-400 mt-0.5">
                        {(file.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={removeFile}
                    className="p-1 rounded-full hover:bg-red-100 active:scale-90 transition-all duration-200 flex-shrink-0 ml-2"
                  >
                    <X className="w-4 h-4 text-red-400" />
                  </button>
                </div>
              ) : (
                // Empty / drag state
                <>
                  <Upload
                    className={`w-8 h-8 sm:w-12 sm:h-12 mx-auto transition-colors ${isDragging ? "text-[#1975FF]" : "text-[#BCBCBC]"}`}
                  />
                  <p className="mt-2 text-sm sm:text-base font-semibold text-[#1F2933] leading-[1.2]">
                    {isDragging
                      ? "Drop your file here"
                      : "Drag your resume here or click to upload"}
                  </p>
                  <p className="mt-1 text-[11px] sm:text-[13px] font-medium text-[#9C9C9C] leading-[1.2]">
                    Acceptable file types: PDF, DOCX (5MB max)
                  </p>
                </>
              )}

              <input
                type="file"
                id="resume"
                accept=".pdf,.docx"
                className="hidden"
                onChange={(e) => {
                  if (e.target.files[0]) handleFile(e.target.files[0]);
                }}
              />
            </label>

            {/* Error message */}
            {error && (
              <p className="mt-1.5 text-xs text-red-500 font-medium">{error}</p>
            )}
            {formErrors.resume && (
              <p className="mt-1.5 text-xs text-red-500 font-medium">
                {formErrors.resume}
              </p>
            )}
          </div>

          <button
            onClick={handleSubmit}
            type="button"
            disabled={jobLoading || !!jobError || submitting}
            className={`w-full py-2.5 sm:py-3 mt-4 md:mt-5 px-3 sm:px-4 rounded-[18px] md:rounded-[22px] font-medium text-[13px] sm:text-base text-white transition-all duration-200 ${
              jobLoading || jobError || submitting
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-[#1975FF] hover:bg-blue-700 hover:shadow-lg hover:-translate-y-0.5 active:scale-[0.98] active:shadow-md"
            }`}
          >
            {submitting
              ? "Submitting..."
              : jobLoading
                ? "Loading Job Details..."
                : "Submit Application"}
          </button>
        </div>
      </div>
      {/* cta */}
      <div className="py-4 sm:py-6 md:py-8 bg-[#F7F7F7B2] transition-all duration-500 hover:bg-[#F0EBFFB2] border border-transparent hover:border-white my-6 md:my-10 px-4">
        <h1 className="max-w-[550px] text-center text-[18px] sm:text-[24px] md:text-[28px] lg:text-[32px] font-semibold mx-auto leading-[1.3]">
          Don't See a Perfect Match?
        </h1>
        <p className="max-w-[500px] font-openSans text-[12px] sm:text-[14px] md:text-[17px] lg:text-[18px] mx-auto text-center mt-3 px-2">
          We're always looking for talented individuals. Send us your resume and
          we'll keep you in mind for future opportunities.
        </p>
        <div className="flex flex-wrap gap-3 sm:gap-4 justify-center mt-6 md:mt-8 px-2">
          <Link
            to="/contact"
            className="bg-[#1975FF] rounded-[10px] py-2 sm:py-2.5 pr-1 sm:pr-2 pl-3 sm:pl-4 flex items-center gap-2 text-[12px] sm:text-[14px] font-semibold text-white hover:bg-[#1560d4] hover:shadow-lg hover:-translate-y-0.5 active:bg-[#1050b8] active:scale-95 transition-all duration-200 shadow-md group"
          >
            Submit General Application{" "}
            <span className="p-1.5 sm:p-2 bg-white rounded-full transition-transform duration-300 group-hover:scale-110">
              <MoveRight
                color="#000"
                className="rotate-[-38deg] w-3 h-3 sm:w-4 sm:h-4"
              />
            </span>
          </Link>
        </div>
      </div>

      {/* Success Modal */}
      {successMessage && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
          <div className="bg-white rounded-[20px] p-6 sm:p-8 max-w-[400px] w-full shadow-2xl animate-scale-in">
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CircleCheck color="#22c55e" className="w-8 h-8" />
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">
                Success!
              </h2>
              <p className="text-gray-600 mb-6">{successMessage}</p>
              <button
                onClick={() => setSuccessMessage("")}
                className="w-full bg-[#1975FF] text-white py-2 sm:py-3 rounded-[10px] font-semibold hover:bg-[#1560d4] transition-all duration-200"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {errorMessage && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
          <div className="bg-white rounded-[20px] p-6 sm:p-8 max-w-[400px] w-full shadow-2xl animate-scale-in">
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <p className="text-2xl font-bold text-red-500">!</p>
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">
                Error
              </h2>
              <p className="text-gray-600 mb-6">{errorMessage}</p>
              <button
                onClick={() => setErrorMessage("")}
                className="w-full bg-red-500 text-white py-2 sm:py-3 rounded-[10px] font-semibold hover:bg-red-600 transition-all duration-200"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        .animate-fade-in {
          animation: fadeIn 0.3s ease-in-out;
        }
        .animate-scale-in {
          animation: scaleIn 0.3s ease-in-out;
        }
      `}</style>

      <Footer />
    </div>
  );
};

export default ApplicationForm;
